import type { TableData } from '../models/chart-data.model';
import type { TableCardConfig } from '../models/table-config.model';
import type {
	TablePagingParams,
	TableRemoteFetchResult,
	TableSortParams,
} from '../models/table-grid-query.types';
import { hasRenderableTablePayload } from '../components/editors/jepto-table-editor/services/table-preview-data.util';
import {
	filterTableColumnarDataToConfiguredFields,
	tableCardHasConfiguredDisplayColumns,
} from './table-column.util';

export type TableCachedSliceDecision = 'absolute-slice' | 'first-page' | 'miss';

/** Grid offset/limit -> 1-based page for Cube `data.offset` / `limit`. */
export function offsetLimitToPage(
	offset: number,
	limit: number
): TablePagingParams {
	const pageSize = Math.max(1, limit);
	return {
		page: Math.floor(Math.max(0, offset) / pageSize) + 1,
		pageSize,
	};
}

/**
 * First-page runtime paging for canvas mount after editor preview paging.
 * Omits `requestId` so CardState does not ignore the write when the id is
 * unchanged (same reason Grid paging omits it in `#commitGridQuery`).
 */
export function tableConfigWithFirstPageOffset(
	config: TableCardConfig
): TableCardConfig {
	const { requestId: _requestId, ...rest } = config as TableCardConfig & {
		requestId?: string;
	};
	return {
		...rest,
		data: {
			...config.data,
			offset: 0,
			limit: config.data?.limit,
		},
	};
}

/** Resolves server total for Grid Pro `totalRowCount`. */
export function resolveTotalRowCount(data: TableData): number {
	const pagination = data.data?.pagination;
	if (typeof pagination?.totalRows === 'number') {
		return pagination.totalRows;
	}
	if (typeof data.data?.total === 'number') {
		return data.data.total;
	}
	const cols = data.data?.result?.columns;
	if (cols) {
		const firstKey = Object.keys(cols)[0];
		if (firstKey) {
			return cols[firstKey]?.length ?? 0;
		}
	}
	return 0;
}

/**
 * True when cached columnar rows can cover the Grid-requested page size at
 * `offset`. Growing page size past the cached server slice must return false
 * so `getCardData` refetches; shrinking or a short final page may still hit.
 */
export function cachedRowsSatisfyRequestedLimit(
	data: TableData,
	offset: number,
	limit: number,
	allowExplicitEmptyPage = false
): boolean {
	const cols = data.data?.result?.columns;
	if (!cols) {
		return false;
	}
	const keys = Object.keys(cols);
	if (keys.length === 0) {
		return false;
	}
	const available = (cols[keys[0]] as unknown[] | undefined)?.length ?? 0;
	if (available <= 0) {
		return (
			allowExplicitEmptyPage && resolveTotalRowCount(data) === 0
		);
	}
	const pageSize = Math.max(1, limit);
	if (available >= pageSize) {
		return true;
	}

	const total = resolveTotalRowCount(data);
	// Entire result set is in memory (including tiny drafts with no pagination meta).
	if (total > 0 && total <= available) {
		return true;
	}
	if (total > available) {
		const remaining = Math.max(0, total - Math.max(0, offset));
		// Short final page: cache holds all remaining rows for this offset.
		if (remaining > 0 && available >= remaining && remaining < pageSize) {
			return true;
		}
	}

	const payloadLimit = data.data?.limit;
	if (
		typeof payloadLimit === 'number' &&
		Number.isFinite(payloadLimit) &&
		payloadLimit === pageSize
	) {
		// Same page size as the cached fetch (possibly a short last page).
		return true;
	}
	return false;
}

/**
 * Whether cached columnar data is for the requested page (offset + enough rows
 * for the requested limit). Larger page sizes must miss so getCardData refetches.
 */
export function tableSliceMatchesRequest(
	config: TableCardConfig,
	data: TableData,
	offset: number,
	limit: number,
	allowExplicitEmptyPage = false
): boolean {
	const expectedOffset = Math.max(0, offset);
	const rawPayloadOffset = data.data?.offset;
	let offsetMatches = false;
	if (
		typeof rawPayloadOffset === 'number' &&
		!Number.isNaN(rawPayloadOffset)
	) {
		offsetMatches = Math.max(0, rawPayloadOffset) === expectedOffset;
	} else {
		// No payload offset: do not use config.offset as a stand-in after a runtime
		// page patch (config moves to page 2 while page-1 rows are still cached).
		const configOffset = Math.max(0, Number(config.data?.offset ?? 0));
		if (configOffset !== expectedOffset) {
			return false;
		}
		offsetMatches = expectedOffset === 0;
	}
	if (!offsetMatches) {
		return false;
	}
	return cachedRowsSatisfyRequestedLimit(
		data,
		expectedOffset,
		limit,
		allowExplicitEmptyPage
	);
}

/**
 * True when cached rows can satisfy a Grid request without a new getCardData,
 * even if CardState `data.offset` already moved (paging in flight).
 */
export function cachedPayloadMatchesOffset(
	data: TableData,
	offset: number
): boolean {
	if (!hasRenderableTablePayload(data)) {
		return false;
	}
	const expectedOffset = Math.max(0, offset);
	const rawPayloadOffset = data.data?.offset;
	if (
		typeof rawPayloadOffset === 'number' &&
		!Number.isNaN(rawPayloadOffset)
	) {
		return Math.max(0, rawPayloadOffset) === expectedOffset;
	}
	// Unstamped first page still in memory while config already advanced.
	return expectedOffset === 0;
}

/** Trim each column array to the page size Grid Pro requested (page 1 window). */
export function sliceColumnsForGridRequest(
	columns: Record<string, Array<string | number | boolean | null>>,
	limit: number
): Record<string, Array<string | number | boolean | null>> {
	return extractPageFromColumnarData(columns, 0, limit);
}

/** Slice every column array to `[offset, offset + limit)`. */
export function extractPageFromColumnarData(
	columns: Record<string, Array<string | number | boolean | null>>,
	offset: number,
	limit: number
): Record<string, Array<string | number | boolean | null>> {
	const pageSize = Math.max(1, limit);
	const start = Math.max(0, offset);
	const end = start + pageSize;
	const out: Record<string, Array<string | number | boolean | null>> = {};
	for (const [key, values] of Object.entries(columns)) {
		out[key] = values.slice(start, end);
	}
	return out;
}

/**
 * True when the cached columnar payload already contains the requested page
 * (typical when the backend returned the full result set in one message).
 */
export function canServeSliceFromCachedColumns(
	data: TableData,
	offset: number,
	limit: number
): boolean {
	const cols = data.data?.result?.columns;
	if (!cols) {
		return false;
	}
	const keys = Object.keys(cols);
	if (keys.length === 0) {
		return false;
	}
	const first = cols[keys[0]] as unknown[] | undefined;
	const available = first?.length ?? 0;
	const start = Math.max(0, offset);
	const pageSize = Math.max(1, limit);
	if (available < start + 1) {
		return false;
	}
	if (available < start + pageSize) {
		return false;
	}
	const total = resolveTotalRowCount(data);
	return total <= available;
}

export function tableOrderMatchesSort(
	order: Record<string, 'asc' | 'desc'> | undefined,
	sort: TableSortParams
): boolean {
	if (sort.direction === null) {
		// Grid unsort: match only when runtime order is empty (ignore column id vs Cube name).
		return !order || Object.keys(order).length === 0;
	}
	if (!sort.column) {
		return !order || Object.keys(order).length === 0;
	}
	if (!order) {
		return false;
	}
	return order[sort.column] === sort.direction;
}

/** Grid request satisfied by in-memory config + payload offset/limit (and sort when present). */
function tableSliceMatchesGridRequest(
	config: TableCardConfig,
	data: TableData,
	offset: number,
	limit: number,
	sortFromGrid: TableSortParams | null,
	allowExplicitEmptyPage = false
): boolean {
	if (
		!tableSliceMatchesRequest(
			config,
			data,
			offset,
			limit,
			allowExplicitEmptyPage
		)
	) {
		return false;
	}
	if (
		sortFromGrid?.column &&
		!tableOrderMatchesSort(config.data?.order, sortFromGrid)
	) {
		return false;
	}
	const sorting = data.data?.result?.sorting;
	if (sortFromGrid?.column && sorting?.column) {
		if (sorting.column !== sortFromGrid.column) {
			return false;
		}
		const wantDir = sortFromGrid.direction;
		if (wantDir && sorting.direction !== wantDir) {
			return false;
		}
	}
	return true;
}

export function sortParamsFromQuerying(
	querying: unknown
): TableSortParams | null {
	const sorting = (
		querying as {
			sorting?: {
				currentSortings?: Array<{
					columnId?: string;
					order?: string | null;
				}>;
				currentSorting?: {
					columnId?: string;
					order?: string | null;
				};
			};
		}
	)?.sorting;
	// After Grid unsort, currentSortings is [] but currentSorting keeps { columnId, order: null }.
	const first =
		sorting?.currentSortings?.[0] ??
		(sorting?.currentSorting?.columnId
			? sorting.currentSorting
			: undefined);
	if (!first?.columnId) {
		return null;
	}
	const order = first.order;
	const direction =
		order === 'asc' ? 'asc' : order === 'desc' ? 'desc' : null;
	return { column: first.columnId, direction };
}

export function canServeCachedTableSlice(params: {
	config: TableCardConfig;
	data: TableData;
	offset: number;
	limit: number;
	sortFromGrid: TableSortParams | null;
	pagingBusy: boolean;
	allowExplicitEmptyPage?: boolean;
}): TableCachedSliceDecision {
	const {
		config,
		data,
		offset,
		limit,
		sortFromGrid,
		pagingBusy,
		allowExplicitEmptyPage = false,
	} = params;
	if (canServeSliceFromCachedColumns(data, offset, limit)) {
		return 'absolute-slice';
	}
	if (
		tableSliceMatchesGridRequest(
			config,
			data,
			offset,
			limit,
			sortFromGrid,
			allowExplicitEmptyPage
		) ||
		(pagingBusy &&
			cachedPayloadMatchesOffset(data, offset) &&
			cachedRowsSatisfyRequestedLimit(
				data,
				offset,
				limit,
				allowExplicitEmptyPage
			))
	) {
		return 'first-page';
	}
	return 'miss';
}

export function toRemoteColumns(
	data: TableData,
	config?: TableCardConfig
): Record<string, Array<string | number | boolean | null>> {
	const cols = data.data?.result?.columns;
	if (!cols) {
		return {};
	}
	const out: Record<string, Array<string | number | boolean | null>> = {};
	for (const [key, values] of Object.entries(cols)) {
		out[key] = values as Array<string | number | boolean | null>;
	}
	return config
		? filterTableColumnarDataToConfiguredFields(config, out)
		: out;
}

export function buildTableRemoteFetchResponse(params: {
	data: TableData;
	config?: TableCardConfig;
	offset: number;
	limit: number;
	window: Exclude<TableCachedSliceDecision, 'miss'>;
}): TableRemoteFetchResult {
	const { data, config, offset, limit, window } = params;
	const remoteColumns = toRemoteColumns(data, config);
	const columns =
		window === 'absolute-slice'
			? extractPageFromColumnarData(remoteColumns, offset, limit)
			: sliceColumnsForGridRequest(remoteColumns, limit);
	return {
		columns,
		totalRowCount: resolveTotalRowCount(data),
	};
}

export function emptyTableRemoteFetchResponse(
	config: TableCardConfig
): TableRemoteFetchResult | null {
	return tableCardHasConfiguredDisplayColumns(config)
		? null
		: { columns: {}, totalRowCount: 0 };
}

export function isAbortFetchError(error: unknown): boolean {
	return error instanceof DOMException && error.name === 'AbortError';
}

export function isFetchAborted(
	signal: AbortSignal | undefined,
	error?: unknown
): boolean {
	return !!signal?.aborted || isAbortFetchError(error);
}

export function isPagingBusyError(error: unknown): boolean {
	return (
		error instanceof Error &&
		error.message.includes('Paging already in flight')
	);
}
